import type { UseQueryOptions, UseQueryResult, UseSuspenseQueryOptions, UseSuspenseQueryResult } from '@tanstack/react-query';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { request } from '../axios.ts';

export enum ChangeLogEntityType {
  PERSON = 'PERSON',
  ADDRESS = 'ADDRESS',
  ADDRESS_CONTACT = 'ADDRESS_CONTACT',
  MEDICATION = 'MEDICATION',
  PERSON_RELATION = 'PERSON_RELATION',
}

export enum ChangeLogSection {
  NONE = 'NONE',
  PERSONAL_DETAILS = 'PERSONAL_DETAILS',
  ANAMNESIS = 'ANAMNESIS',
  DIAGNOSTICS = 'DIAGNOSTICS',
  THERAPY = 'THERAPY',
  CONTACT_TRACING = 'CONTACT_TRACING',
  NETWORK = 'NETWORK',
  TRACING_CONTACT = 'TRACING_CONTACT',
  CASE_MANAGEMENT = 'CASE_MANAGEMENT',
  MASTER_PERSON = 'MASTER_PERSON',
  ADDRESS = 'ADDRESS',
  BEZUGSPERSON = 'BEZUGSPERSON',
  CONTACT_PERSON = 'CONTACT_PERSON',
}

export type ChangeLogFieldChangeDto = {
  entityType: string | null;
  subType: string | null;
  parentSubType?: string | null;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
};

export type ChangeLogEntryDto = {
  changeSetId: string;
  changedAt: string;
  changedBy: string | null;
  section: string | null;
  entityType: string | null;
  rootEntityType: string | null;
  rootEntityId: string;
  subType: string | null;
  operation: string | null;
  changes: ChangeLogFieldChangeDto[];
};

type RequestOptions = Parameters<typeof request>[1];

export function getChangeLogByEntity(
  entityType: ChangeLogEntityType,
  entityId: string,
  options?: RequestOptions,
  signal?: AbortSignal,
) {
  return request<ChangeLogEntryDto[]>(
    {
      url: `/api/change-log/entities/${entityType}/${entityId}`,
      method: 'GET',
      signal,
    },
    options,
  );
}

export function getChangeLogByEntityQueryKey(entityType: ChangeLogEntityType, entityId: string) {
  return ['/api/change-log/entities', entityType, entityId] as const;
}

export function getChangeLogByPatient(patientId: string, options?: RequestOptions, signal?: AbortSignal) {
  return request<ChangeLogEntryDto[]>(
    {
      url: `/api/change-log/patients/${patientId}`,
      method: 'GET',
      signal,
    },
    options,
  );
}

export function getChangeLogByPatientQueryKey(patientId: string) {
  return ['/api/change-log/patients', patientId] as const;
}

export function useGetChangeLogByPatientSuspense(
  patientId: string,
  options?: {
    query?: Partial<UseSuspenseQueryOptions<ChangeLogEntryDto[]>>;
    request?: RequestOptions;
  },
): UseSuspenseQueryResult<ChangeLogEntryDto[]> & {
  queryKey: ReturnType<typeof getChangeLogByPatientQueryKey>;
} {
  const queryKey = getChangeLogByPatientQueryKey(patientId);

  const query = useSuspenseQuery({
    queryKey,
    queryFn: ({ signal }) => getChangeLogByPatient(patientId, options?.request, signal),
    ...options?.query,
  }) as UseSuspenseQueryResult<ChangeLogEntryDto[]> & {
    queryKey: ReturnType<typeof getChangeLogByPatientQueryKey>;
  };

  query.queryKey = queryKey;
  return query;
}

export function useGetChangeLogByEntitySuspense(
  entityType: ChangeLogEntityType,
  entityId: string,
  options?: {
    query?: Partial<UseSuspenseQueryOptions<ChangeLogEntryDto[]>>;
    request?: RequestOptions;
  },
): UseSuspenseQueryResult<ChangeLogEntryDto[]> & {
  queryKey: ReturnType<typeof getChangeLogByEntityQueryKey>;
} {
  const queryKey = getChangeLogByEntityQueryKey(entityType, entityId);

  const query = useSuspenseQuery({
    queryKey,
    queryFn: ({ signal }) => getChangeLogByEntity(entityType, entityId, options?.request, signal),
    ...options?.query,
  }) as UseSuspenseQueryResult<ChangeLogEntryDto[]> & {
    queryKey: ReturnType<typeof getChangeLogByEntityQueryKey>;
  };

  query.queryKey = queryKey;
  return query;
}

export function useGetChangeLogByEntity(
  entityType: ChangeLogEntityType,
  entityId: string | undefined,
  options?: {
    enabled?: boolean;
    query?: Partial<UseQueryOptions<ChangeLogEntryDto[]>>;
    request?: RequestOptions;
  },
): UseQueryResult<ChangeLogEntryDto[]> {
  const enabled = (options?.enabled ?? true) && !!entityId;

  return useQuery({
    queryKey: getChangeLogByEntityQueryKey(entityType, entityId ?? ''),
    queryFn: ({ signal }) => getChangeLogByEntity(entityType, entityId!, options?.request, signal),
    enabled,
    ...options?.query,
  });
}
